import { useEffect, useRef, useState } from "react";
import QrScanner from "qr-scanner";
import { BrandButton } from "../components/ui/BrandButton";
import { useAppLoading } from "../context/AppLoadingContext";
import { redeemTnuaQr } from "../services/tnuaQrService";

QrScanner.WORKER_PATH = new URL("qr-scanner/qr-scanner-worker.min.js", import.meta.url).toString();

export function ScanQrPage() {
  const { trackAsync } = useAppLoading();
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const lastScanRef = useRef({ data: "", at: 0 });
  const isSubmittingRef = useRef(false);
  const [status, setStatus] = useState("loading");
  const [canUseCamera, setCanUseCamera] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizeQrData = (result) => {
    const data = typeof result === "string" ? result : result?.data ?? "";
    return data.trim();
  };

  const clearResumeTimeout = () => {
    if (!resumeTimeoutRef.current) {
      return;
    }

    window.clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = null;
  };

  useEffect(() => {
    let isMounted = true;

    const getCameraErrorMessage = (cause) => {
      if (!window.isSecureContext) {
        return "La cámara necesita que la app esté abierta en HTTPS o en localhost.";
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        return "Este navegador no permite acceder a la cámara del dispositivo.";
      }

      if (cause?.name === "NotAllowedError" || cause?.name === "PermissionDeniedError") {
        return "Necesitamos permiso para usar la cámara. Revisá los permisos del navegador.";
      }

      if (cause?.name === "NotFoundError" || cause?.message === "Camera not found.") {
        return "No encontramos una cámara disponible en este dispositivo.";
      }

      return cause?.message || "No pudimos activar la cámara.";
    };

    const initializeScanner = async () => {
      try {
        if (!window.isSecureContext) {
          setStatus("no-camera");
          setCanUseCamera(false);
          return;
        }

        const cameraAvailable = await QrScanner.hasCamera();

        if (!isMounted) {
          return;
        }

        setCanUseCamera(cameraAvailable);

        if (!cameraAvailable) {
          setStatus("no-camera");
          return;
        }

        const scanner = new QrScanner(
          videoRef.current,
          async (result) => {
            const normalized = normalizeQrData(result);

            if (!normalized || isSubmittingRef.current) {
              return;
            }

            const { data: lastData, at: lastAt } = lastScanRef.current;
            const now = Date.now();
            if (normalized === lastData && now - lastAt < 2000) {
              return;
            }

            lastScanRef.current = { data: normalized, at: now };
            await scanner.pause(true);
            await handleSubmit(normalized);
            clearResumeTimeout();

            resumeTimeoutRef.current = window.setTimeout(async () => {
              if (!isMounted || !scannerRef.current) {
                return;
              }

              try {
                await scannerRef.current.start();
                setStatus("scanning");
              } catch (resumeError) {
                setStatus("error");
                setError(
                  resumeError?.message || "No pudimos volver a activar la cámara.",
                );
              }
            }, 1000);
          },
          {
            preferredCamera: "environment",
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 8,
            returnDetailedScanResult: true,
          },
        );

        scannerRef.current = scanner;
        await scanner.start();
        setStatus("scanning");
      } catch (startError) {
        if (!isMounted) {
          return;
        }

        setStatus("error");
        setError(getCameraErrorMessage(startError));
      }
    };

    void trackAsync(initializeScanner);

    return () => {
      isMounted = false;
      clearResumeTimeout();

      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
    };
  }, [trackAsync]);

  const restartScanner = async () => {
    clearResumeTimeout();
    setError("");
    setSuccess(null);

    if (!scannerRef.current) {
      setStatus(canUseCamera ? "scanning" : "no-camera");
      return;
    }

    try {
      await scannerRef.current.start();
      setStatus("scanning");
    } catch (startError) {
      setStatus("error");
      setError(
        !window.isSecureContext
          ? "La cámara necesita que la app esté abierta en HTTPS o en localhost."
          : startError?.message || "No pudimos volver a activar la cámara.",
      );
    }
  };

  async function handleSubmit(rawValue) {
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    setError("");

    try {
      const response = await redeemTnuaQr({ qrToken: rawValue });
      setSuccess(response.redemption);
      setStatus("scanning");
    } catch (requestError) {
      setError(requestError.message || "No pudimos validar el QR.");
      setStatus("scanning");
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  }

  const showCameraBox = status !== "no-camera";

  return (
    <div className="grid gap-5">
      <section className="px-1 py-1 sm:px-2 sm:py-2">
        <h2 className="font-display text-3xl text-brand-primary sm:text-4xl">
          Escanear QR
        </h2>
      </section>

      <section className="grid gap-4 rounded-[2rem] border border-brand-secondary/15 bg-white p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="mt-1 text-sm text-slate-600">
              Apuntá la cámara al QR de tu tnua para validar el bonus. El escaneo se hace desde la cámara del dispositivo.
            </p>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="relative overflow-hidden rounded-[2rem] border border-brand-secondary/20 bg-slate-950">
          {showCameraBox ? (
            <>
              <video
                ref={videoRef}
                muted
                autoPlay
                playsInline
                className="h-[24rem] w-full object-cover sm:h-[34rem]"
              />
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(15,23,42,0.18),rgba(15,23,42,0.48))]" />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="h-56 w-56 rounded-[2rem] border-2 border-brand-gold shadow-[0_0_0_9999px_rgba(15,23,42,0.18)]" />
              </div>
              {status === "loading" ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-6 text-center">
                  <div className="grid gap-3">
                    <p className="text-lg font-semibold text-white">
                      Preparando cámara...
                    </p>
                    <p className="text-sm text-white/80">
                      Si no aparece, revisá permisos y asegurate de estar en HTTPS.
                    </p>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="flex min-h-[24rem] items-center justify-center px-6 py-10 text-center text-white sm:min-h-[34rem]">
              <div className="grid gap-4">
                <p className="text-lg font-semibold">
                  {status === "loading" ? "Preparando cámara..." : "La cámara no está disponible."}
                </p>
                <div className="flex justify-center">
                  <BrandButton type="button" onClick={restartScanner} variant="outline" radius="full">
                    Reintentar cámara
                  </BrandButton>
                </div>
              </div>
            </div>
          )}
        </div>

        {success ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            Sumaste 2 puntos por la tnua {success.tnua.name} el {success.businessDate}.
          </div>
        ) : null}

        <div className="flex flex-wrap gap-3">
          <BrandButton
            type="button"
            onClick={restartScanner}
            variant="secondary"
            radius="full"
            disabled={isSubmitting}
          >
            Escanear otra vez
          </BrandButton>
        </div>
      </section>
    </div>
  );
}
